import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { FF_NUMBER, IStarter, LEAD_AREAS, STARTERS, VOICES } from './data'
import { useCyclingPlaceholder } from './hooks'
import { AppShell, TopBar } from './Frame'
import { ILaunched, useFirstRun } from './store'
import { Button, Icon, VoiceOrb } from './ui'

const tones = {
   blue: { card: 'border-[#d6e2ff] bg-[var(--fr-blue-tint)]', dot: 'bg-[var(--fr-blue)]', text: 'text-[var(--fr-blue-dark)]' },
   warm: { card: 'border-[#f5e1bf] bg-[var(--fr-warn-tint)]', dot: 'bg-[var(--fr-warn)]', text: 'text-[var(--fr-warn)]' },
   violet: { card: 'border-[#e2d6ff] bg-[var(--fr-pond-tint)]', dot: 'bg-[var(--fr-pond)]', text: 'text-[var(--fr-pond)]' },
   green: { card: 'border-[#cdeedb] bg-[var(--fr-good-tint)]', dot: 'bg-[var(--fr-good)]', text: 'text-[var(--fr-good)]' },
   teal: { card: 'border-[#c9ebe5] bg-[var(--fr-source-tint)]', dot: 'bg-[var(--fr-source)]', text: 'text-[var(--fr-source)]' },
   grey: { card: 'border-[var(--fr-border)] bg-white', dot: 'bg-[var(--fr-text)]', text: 'text-[var(--fr-text)]' },
} as const
type TTone = keyof typeof tones
const starterTone = (st: IStarter): TTone => (st.kind === 'new' ? 'blue' : st.kind === 'older' ? 'warm' : 'violet')

/* ---------- Before the first campaign: one question and three ready campaigns ---------- */

const FirstHome = () => {
   const navigate = useNavigate()
   const { startDraft, businessSent, calendar, owner, areaCodes } = useFirstRun()
   const [text, setText] = useState('')
   const placeholder = useCyclingPlaceholder(!text)
   const first = owner.name.split(' ')[0]
   const go = (from: { text?: string; starter?: IStarter }) => {
      startDraft(from)
      navigate('/start/campaign')
   }
   const status = [
      { id: 'texting', tone: businessSent ? 'warm' : 'grey', icon: 'message', title: businessSent ? 'Texting approval pending' : 'Texting is off', body: businessSent ? 'Carriers take 3 to 5 days. Calls and voicemails run now.' : 'Confirm your business details and we file for approval.', action: businessSent ? undefined : { label: 'Confirm details', onClick: () => navigate('/start?step=business') }, state: businessSent ? 'wait' : 'todo' },
      { id: 'calendar', tone: calendar ? 'green' : 'grey', icon: 'calendar', title: calendar ? `${calendar === 'google' ? 'Google' : 'Outlook'} Calendar connected` : 'No calendar yet', body: calendar ? 'Appointments land on your calendar and in Follow Up Boss.' : 'Appointments are written into Follow Up Boss for now.', action: calendar ? undefined : { label: 'Connect calendar', onClick: () => navigate('/start?step=calendar') }, state: calendar ? 'done' : 'todo' },
      { id: 'number', tone: 'teal', icon: 'phone', title: 'Your number is saved', body: `${FF_NUMBER} · campaigns call from (${areaCodes.join('), (')}) near ${LEAD_AREAS[0].city}.`, state: 'done' },
   ] as const
   return (
      <div className="fr fixed inset-0 flex flex-col overflow-hidden" data-testid="home">
         <TopBar />
         <main className="flex-1 min-h-0 overflow-y-auto fr-scroll">
            <div className="mx-auto max-w-[960px] px-8 py-8 flex flex-col items-center min-h-full justify-center gap-7">
               <section className="text-center w-full flex flex-col items-center">
                  <h1 className="text-[36px] md:text-[44px] leading-[1.1] font-semibold tracking-[-0.02em] [text-wrap:balance]">Hi {first}, who should we call first?</h1>
                  <p className="mt-2 text-[16px] text-[var(--fr-text-2)]">Describe the leads in your own words, or start from a ready campaign.</p>
                  <div className="mt-7 w-full max-w-[680px] fr-glow" data-testid="home-glow">
                   <div className="relative rounded-full bg-white">
                     <input
                        value={text}
                        onChange={(e) => setText(e.currentTarget.value)}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter' && text.trim()) go({ text: text.trim() })
                        }}
                        placeholder={placeholder}
                        data-testid="home-describe"
                        className="w-full h-16 rounded-full bg-white pl-7 pr-16 text-[17px] focus:outline-none"
                     />
                     <button type="button" aria-label="Set up this campaign" disabled={!text.trim()} onClick={() => go({ text: text.trim() })} data-testid="home-go" className={cn('absolute right-2.5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-white transition-colors', text.trim() ? 'bg-[var(--fr-blue)] hover:bg-[var(--fr-blue-dark)]' : 'bg-[var(--fr-border-strong)]')}>
                        <Icon name="chevrons" size={18} />
                     </button>
                   </div>
                  </div>
               </section>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full" data-testid="starter-cards">
                  {STARTERS.map((st) => {
                     const t = tones[starterTone(st)]
                     return (
                        <button key={st.id} type="button" onClick={() => go({ starter: st })} data-testid={`starter-${st.id}`} className={cn('relative text-left rounded-[20px] border p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--fr-shadow)] flex flex-col gap-2 min-h-[164px]', t.card)}>
                           <span className="flex items-center justify-between">
                              <span className={cn('inline-flex items-center justify-center w-10 h-10 rounded-full text-white', t.dot)}>
                                 <Icon name={st.kind === 'new' ? 'zap' : st.kind === 'older' ? 'clock' : 'users'} size={17} />
                              </span>
                              {st.recommended && <span className={cn('text-[11px] font-semibold uppercase tracking-[0.08em]', t.text)}>Start here</span>}
                           </span>
                           <span className="block text-[16px] font-semibold mt-1">{st.title}</span>
                           <span className="block text-[13px] leading-5 text-[var(--fr-text-2)]">{st.description}</span>
                           <span className="mt-auto flex items-center justify-between pt-1">
                              <span className="text-[13px]"><b>{st.leads.toLocaleString()}</b> <span className="text-[var(--fr-text-2)]">leads match today</span></span>
                              <span className={cn('inline-flex items-center justify-center w-8 h-8 rounded-full bg-white', t.text)}><Icon name="arrow-right" size={14} /></span>
                           </span>
                        </button>
                     )
                  })}
               </div>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
                  {status.map((c) => {
                     const t = tones[c.tone]
                     return (
                        <div key={c.id} className="rounded-2xl border border-[var(--fr-border)] bg-white p-4 flex items-start gap-3" data-testid={`${c.id}-card`} data-state={c.state}>
                           <span className={cn('flex shrink-0 items-center justify-center w-9 h-9 rounded-full text-white', t.dot)}><Icon name={c.icon} size={15} /></span>
                           <span className="min-w-0">
                              <span className="block text-[14px] font-semibold truncate">{c.title}</span>
                              <span className="block text-[12px] leading-4 text-[var(--fr-text-2)] mt-0.5">{c.body}</span>
                              {'action' in c && c.action && (
                                 <button type="button" onClick={c.action.onClick} className="mt-1.5 text-[12px] font-semibold text-[var(--fr-blue)] hover:underline">{c.action.label}</button>
                              )}
                           </span>
                        </div>
                     )
                  })}
               </div>
            </div>
         </main>
      </div>
   )
}

/* ---------- After the first campaign: the campaigns page ---------- */

const n = (v: number) => v.toLocaleString()
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '0%')
const ago = (t: number) => {
   const m = Math.max(0, Math.round((Date.now() - t) / 60000))
   if (m < 1) return 'Just now'
   if (m < 60) return `${m} min ago`
   const h = Math.round(m / 60)
   if (h < 24) return `${h} h ago`
   return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const StatCard = ({ label, value, sub, tone, icon }: { label: string; value: string; sub?: string; tone: TTone; icon: string }) => (
   <div className="rounded-2xl border border-[var(--fr-border)] bg-white p-4 flex items-start gap-3 min-w-0">
      <span className={cn('flex shrink-0 items-center justify-center w-9 h-9 rounded-full text-white', tones[tone].dot)}><Icon name={icon} size={15} /></span>
      <span className="min-w-0">
         <span className="block text-[12px] font-medium text-[var(--fr-text-2)] truncate">{label}</span>
         <span className="block text-[24px] leading-7 font-semibold tracking-tight mt-0.5">{value}</span>
         {sub && <span className={cn('block text-[12px] mt-0.5', tones[tone].text)}>{sub}</span>}
      </span>
   </div>
)

const CampaignsHome = ({ campaigns }: { campaigns: ILaunched[] }) => {
   const navigate = useNavigate()
   const { startDraft, toggleCampaign, owner, businessSent } = useFirstRun()
   const totals = campaigns.reduce((t, c) => ({ calls: t.calls + c.stats.calls, answered: t.answered + c.stats.answered, conversations: t.conversations + c.stats.conversations, appointments: t.appointments + c.stats.appointments, transfers: t.transfers + c.stats.transfers, leads: t.leads + c.leads }), { calls: 0, answered: 0, conversations: 0, appointments: 0, transfers: 0, leads: 0 })
   const live = campaigns.filter((c) => c.status === 'live').length
   const create = () => {
      startDraft({})
      navigate('/start/campaign')
   }
   const voiceOf = (c: ILaunched) => VOICES.find((v) => v.id === c.voiceId) || VOICES[0]
   const cols = 'grid grid-cols-[minmax(220px,1.8fr)_92px_repeat(5,minmax(80px,1fr))_100px_96px] items-center gap-3 px-5'
   return (
      <AppShell testId="home">
            <div className="mx-auto max-w-[1180px] px-8 py-7">
               <div className="flex items-end justify-between gap-4">
                  <div>
                     <h1 className="text-[30px] leading-[1.1] font-semibold tracking-[-0.02em]">Campaigns</h1>
                     <p className="mt-1.5 text-[14px] text-[var(--fr-text-2)]" data-testid="campaigns-summary">
                        {live} {live === 1 ? 'campaign' : 'campaigns'} live · {n(totals.leads)} leads in play · interested leads go to {owner.phone || 'your mobile'}
                        {!businessSent && <> · <button type="button" onClick={() => navigate('/start?step=business')} className="font-semibold text-[var(--fr-blue)] hover:underline">turn on texting</button></>}
                     </p>
                  </div>
                  <Button onClick={create} testId="create-campaign"><Icon name="plus" size={16} /> Create new campaign</Button>
               </div>

               <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="overall-stats">
                  <StatCard label="Calls made" value={n(totals.calls)} sub="This week" tone="blue" icon="phone" />
                  <StatCard label="Answered" value={n(totals.answered)} sub={`${pct(totals.answered, totals.calls)} pick-up`} tone="teal" icon="voice" />
                  <StatCard label="Conversations" value={n(totals.conversations)} sub={`${pct(totals.conversations, totals.answered)} of answered`} tone="violet" icon="message" />
                  <StatCard label="Appointments" value={n(totals.appointments)} sub="On your calendar" tone="green" icon="calendar" />
                  <StatCard label="Live transfers" value={n(totals.transfers)} sub={`To ${owner.name.split(' ')[0]}`} tone="warm" icon="zap" />
               </div>

               <div className="mt-6 rounded-[20px] border border-[var(--fr-border)] bg-white overflow-hidden" data-testid="campaign-table">
                  <div className={cn(cols, 'h-11 bg-[var(--fr-surface-2)] text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--fr-text-3)]')}>
                     <span>Campaign</span>
                     <span>Status</span>
                     <span className="text-right">Leads</span>
                     <span className="text-right">Calls</span>
                     <span className="text-right">Answered</span>
                     <span className="text-right">Appts</span>
                     <span className="text-right">Transfers</span>
                     <span className="pl-2">Launched</span>
                     <span />
                  </div>
                  {campaigns.map((c) => {
                     const v = voiceOf(c)
                     const on = c.status === 'live'
                     return (
                        <div key={c.id} className={cn(cols, 'min-h-[72px] border-t border-[var(--fr-border)] text-[14px]')} data-testid={`campaign-row-${c.id}`} data-state={c.status}>
                           <span className="flex items-center gap-3 min-w-0">
                              <VoiceOrb palette={v.palette} size={36} live={on} />
                              <span className="min-w-0">
                                 <span className="block font-semibold truncate">{c.name}</span>
                                 <span className="block text-[12px] text-[var(--fr-text-2)] truncate">{c.assistantName} · from ({c.areaCodes.join('), (')})</span>
                              </span>
                           </span>
                           <span>
                              <span className={cn('inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] font-semibold', on ? 'bg-[var(--fr-good-tint)] text-[var(--fr-good)]' : 'bg-[var(--fr-surface-2)] text-[var(--fr-text-2)]')}>
                                 <span className={cn('w-1.5 h-1.5 rounded-full', on ? 'bg-[var(--fr-good)]' : 'bg-[var(--fr-text-3)]')} /> {on ? 'Live' : 'Paused'}
                              </span>
                           </span>
                           <span className="text-right tabular-nums">{n(c.leads)}</span>
                           <span className="text-right tabular-nums">{n(c.stats.calls)}</span>
                           <span className="text-right tabular-nums">{n(c.stats.answered)} <span className="text-[12px] text-[var(--fr-text-3)]">{pct(c.stats.answered, c.stats.calls)}</span></span>
                           <span className="text-right tabular-nums font-semibold text-[var(--fr-good)]">{n(c.stats.appointments)}</span>
                           <span className="text-right tabular-nums">{n(c.stats.transfers)}</span>
                           <span className="pl-2 text-[13px] text-[var(--fr-text-2)]">{ago(c.launchedAt)}</span>
                           <span className="flex justify-end">
                              <Button size="sm" variant="secondary" onClick={() => toggleCampaign(c.id)} testId={`toggle-${c.id}`}>
                                 <Icon name={on ? 'pause' : 'play'} size={12} /> {on ? 'Pause' : 'Resume'}
                              </Button>
                           </span>
                        </div>
                     )
                  })}
               </div>
               <p className="mt-3 text-[12px] text-[var(--fr-text-3)]">Numbers update as calls go out. Tap a campaign to see every conversation.</p>
            </div>
      </AppShell>
   )
}

/** Home: the first-campaign page until something is live, then the campaigns page. */
export const Home = () => {
   const { launched } = useFirstRun()
   return launched.length ? <CampaignsHome campaigns={launched} /> : <FirstHome />
}

/* ---------- The other pages in the nav, as UI only ---------- */

export const StubPage = ({ page }: { page: string }) => {
   const navigate = useNavigate()
   const s = useFirstRun()
   const seats = 1 + s.selectedTeam.length + s.invited.length
   if (page === 'dashboard') return <DashboardPage />
   if (page === 'settings') {
      const rows = [
         { icon: 'users', title: 'Team', value: seats === 1 ? 'Just you' : `${seats} seats`, step: 'team' },
         { icon: 'calendar', title: 'Calendar', value: s.calendar ? (s.calendar === 'google' ? 'Google Calendar' : 'Outlook Calendar') : 'Not connected', step: 'calendar' },
         { icon: 'gauge', title: 'Monthly budget', value: `$${s.budget}/mo`, step: 'budget' },
         { icon: 'pin', title: 'Area codes', value: s.areaCodes.map((c) => `(${c})`).join(', '), step: 'area' },
         { icon: 'shield', title: 'Leads we leave alone', value: `${Object.values(s.stops).flat().length} rules`, step: 'stops-working' },
         { icon: 'file', title: 'Texting approval', value: s.businessSent ? 'Pending, 3 to 5 days' : 'Not sent', step: 'business' },
      ]
      return (
         <AppShell testId="page-settings">
               <div className="mx-auto max-w-[720px] px-8 py-7">
                  <h1 className="text-[30px] leading-[1.1] font-semibold tracking-[-0.02em]">Settings</h1>
                  <p className="mt-1.5 text-[14px] text-[var(--fr-text-2)]">Everything from setup, editable any time.</p>
                  <div className="mt-6 rounded-[20px] border border-[var(--fr-border)] bg-white divide-y divide-[var(--fr-border)] overflow-hidden">
                     {rows.map((r) => (
                        <button key={r.step} type="button" onClick={() => navigate(`/start?step=${r.step}`)} className="w-full flex items-center gap-4 px-5 min-h-[64px] text-left hover:bg-[var(--fr-surface-2)]">
                           <span className="flex shrink-0 items-center justify-center w-10 h-10 rounded-full bg-[var(--fr-blue-tint)] text-[var(--fr-blue-dark)]"><Icon name={r.icon} size={16} /></span>
                           <span className="flex-1 min-w-0">
                              <span className="block text-[15px] font-semibold">{r.title}</span>
                              <span className="block text-[13px] text-[var(--fr-text-2)] truncate">{r.value}</span>
                           </span>
                           <span className="text-[13px] font-semibold text-[var(--fr-blue)]">Edit</span>
                        </button>
                     ))}
                  </div>
               </div>
         </AppShell>
      )
   }
   const title = page === 'numbers' ? 'Phone numbers' : 'Conversations'
   const body = page === 'numbers' ? `Your campaigns call from local numbers in (${s.areaCodes.join('), (')}). Your own line for transfers and help is ${FF_NUMBER}.` : 'Every call and text shows up here as soon as it happens, with the transcript and what the lead said.'
   return (
      <AppShell testId={`page-${page}`}>
         <div className="h-full flex items-center justify-center px-8">
            <div className="max-w-[440px] text-center flex flex-col items-center">
               <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[var(--fr-blue-tint)] text-[var(--fr-blue-dark)]"><Icon name={page === 'numbers' ? 'phone' : 'message'} size={26} /></span>
               <h1 className="mt-5 text-[30px] leading-[1.1] font-semibold tracking-[-0.02em]">{title}</h1>
               <p className="mt-2 text-[15px] leading-6 text-[var(--fr-text-2)]">{body}</p>
               <Button variant="secondary" className="mt-6" onClick={() => navigate('/start/home')}>Back to campaigns</Button>
            </div>
         </div>
      </AppShell>
   )
}

/* ---------- Dashboard: the overview page in the nav ---------- */

const DashboardPage = () => {
   const navigate = useNavigate()
   const { launched, owner, businessSent, calendar, areaCodes, startDraft } = useFirstRun()
   const first = owner.name.split(' ')[0]
   const totals = launched.reduce((t, c) => ({ calls: t.calls + c.stats.calls, answered: t.answered + c.stats.answered, conversations: t.conversations + c.stats.conversations, appointments: t.appointments + c.stats.appointments, transfers: t.transfers + c.stats.transfers }), { calls: 0, answered: 0, conversations: 0, appointments: 0, transfers: 0 })
   const live = launched.filter((c) => c.status === 'live').length
   const cards = [
      { id: 'texting', tone: businessSent ? 'warm' : 'grey', icon: 'message', title: businessSent ? 'Texting approval pending' : 'Texting is off', body: businessSent ? 'Carriers take 3 to 5 days. Calls and voicemails run now.' : 'Confirm your business details and we file for approval.' },
      { id: 'calendar', tone: calendar ? 'green' : 'grey', icon: 'calendar', title: calendar ? `${calendar === 'google' ? 'Google' : 'Outlook'} Calendar connected` : 'No calendar yet', body: calendar ? 'Appointments land on your calendar and in Follow Up Boss.' : 'Appointments are written into Follow Up Boss for now.' },
      { id: 'number', tone: 'teal', icon: 'phone', title: 'Your number is saved', body: `${FF_NUMBER} · campaigns call from (${areaCodes.join('), (')}).` },
   ] as const
   return (
      <AppShell testId="page-dashboard">
         <div className="mx-auto max-w-[1180px] px-8 py-7">
            <div className="flex items-end justify-between gap-4">
               <div>
                  <h1 className="text-[30px] leading-[1.1] font-semibold tracking-[-0.02em]">Hi {first}</h1>
                  <p className="mt-1.5 text-[14px] text-[var(--fr-text-2)]">{live ? `${live} ${live === 1 ? 'campaign is' : 'campaigns are'} calling your leads right now.` : 'No campaign is live yet. Create one and the numbers start moving.'}</p>
               </div>
               <Button onClick={() => { startDraft({}); navigate('/start/campaign') }}><Icon name="plus" size={16} /> Create new campaign</Button>
            </div>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
               <StatCard label="Calls made" value={n(totals.calls)} sub="This week" tone="blue" icon="phone" />
               <StatCard label="Answered" value={n(totals.answered)} sub={`${pct(totals.answered, totals.calls)} pick-up`} tone="teal" icon="voice" />
               <StatCard label="Conversations" value={n(totals.conversations)} sub={`${pct(totals.conversations, totals.answered)} of answered`} tone="violet" icon="message" />
               <StatCard label="Appointments" value={n(totals.appointments)} sub="On your calendar" tone="green" icon="calendar" />
               <StatCard label="Live transfers" value={n(totals.transfers)} sub={`To ${first}`} tone="warm" icon="zap" />
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
               {cards.map((c) => (
                  <div key={c.id} className="rounded-2xl border border-[var(--fr-border)] bg-white p-4 flex items-start gap-3">
                     <span className={cn('flex shrink-0 items-center justify-center w-9 h-9 rounded-full text-white', tones[c.tone].dot)}><Icon name={c.icon} size={15} /></span>
                     <span className="min-w-0">
                        <span className="block text-[14px] font-semibold truncate">{c.title}</span>
                        <span className="block text-[12px] leading-4 text-[var(--fr-text-2)] mt-0.5">{c.body}</span>
                     </span>
                  </div>
               ))}
            </div>
         </div>
      </AppShell>
   )
}
